import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from '@/layouts/MainLayout';
import RouteGuard from './RouteGuard';
import LoadingScreen from '@/components/common/LoadingScreen';
import { ROUTES } from './paths';
import { PERMISSIONS as P } from '@/permissions/permissions';

// Lazy-load every page so each route is its own code-split chunk.
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const UsersListPage = lazy(() => import('@/pages/users/UsersListPage'));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'));
const NewsListPage = lazy(() => import('@/pages/news/NewsListPage'));
const NewsDetailPage = lazy(() => import('@/pages/news/NewsDetailPage'));
const ScraperPage = lazy(() => import('@/pages/scraper/ScraperPage'));
const QuizListPage = lazy(() => import('@/pages/quiz/QuizListPage'));
const QuizDetailPage = lazy(() => import('@/pages/quiz/QuizDetailPage'));
const MainsListPage = lazy(() => import('@/pages/mains/MainsListPage'));
const MainsDetailPage = lazy(() => import('@/pages/mains/MainsDetailPage'));
const MainsQuestionsListPage = lazy(() => import('@/pages/mains/MainsQuestionsListPage'));
const MainsQuestionFormPage = lazy(() => import('@/pages/mains/MainsQuestionFormPage'));
const ChatSessionsPage = lazy(() => import('@/pages/ai/ChatSessionsPage'));
const AiUsagePage = lazy(() => import('@/pages/ai/AiUsagePage'));
const CategoriesPage = lazy(() => import('@/pages/categories/CategoriesPage'));
const ExamsPage = lazy(() => import('@/pages/exams/ExamsPage'));
const AlertsListPage = lazy(() => import('@/pages/alerts/AlertsListPage'));
const AlertFormPage = lazy(() => import('@/pages/alerts/AlertFormPage'));
const DownloadsPage = lazy(() => import('@/pages/downloads/DownloadsPage'));
const BannersPage = lazy(() => import('@/pages/banners/BannersPage'));
const PaymentsListPage = lazy(() => import('@/pages/payments/PaymentsListPage'));
const SubscriptionsPage = lazy(() => import('@/pages/payments/SubscriptionsPage'));
const RecurringSubscriptionsPage = lazy(() => import('@/pages/subscriptions/RecurringSubscriptionsPage'));
const RenewalsPage = lazy(() => import('@/pages/subscriptions/RenewalsPage'));
const WebhookLogsPage = lazy(() => import('@/pages/subscriptions/WebhookLogsPage'));
const CouponsPage = lazy(() => import('@/pages/coupons/CouponsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/error/ForbiddenPage'));

// Helper: wrap a lazy element in the auth + permission guard.
const guard = (permission, element) => <RouteGuard permission={permission}>{element}</RouteGuard>;

export default function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen height="100vh" />}>
      <Routes>
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />

        <Route element={<MainLayout />}>
          <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          <Route path={ROUTES.DASHBOARD} element={guard(P.DASHBOARD_READ, <DashboardPage />)} />

          <Route path={ROUTES.USERS} element={guard(P.USERS_READ, <UsersListPage />)} />
          <Route path={ROUTES.USER_DETAIL()} element={guard(P.USERS_READ, <UserDetailPage />)} />

          <Route path={ROUTES.NEWS} element={guard(P.CONTENT_READ, <NewsListPage />)} />
          <Route path={ROUTES.NEWS_DETAIL()} element={guard(P.CONTENT_READ, <NewsDetailPage />)} />
          <Route path={ROUTES.SCRAPER} element={guard(P.SCRAPER_READ, <ScraperPage />)} />

          <Route path={ROUTES.QUIZZES} element={guard(P.QUIZ_READ, <QuizListPage />)} />
          <Route path={ROUTES.QUIZ_DETAIL()} element={guard(P.QUIZ_READ, <QuizDetailPage />)} />
          <Route path={ROUTES.MAINS} element={guard(P.MAINS_READ, <MainsListPage />)} />
          <Route path={ROUTES.MAINS_DETAIL()} element={guard(P.MAINS_READ, <MainsDetailPage />)} />
          <Route path={ROUTES.MAINS_QUESTIONS} element={guard(P.MAINS_READ, <MainsQuestionsListPage />)} />
          <Route path={ROUTES.MAINS_QUESTION_NEW} element={guard(P.MAINS_WRITE, <MainsQuestionFormPage />)} />
          <Route path={ROUTES.MAINS_QUESTION_DETAIL()} element={guard(P.MAINS_WRITE, <MainsQuestionFormPage />)} />

          <Route path={ROUTES.CHATS} element={guard(P.AI_READ, <ChatSessionsPage />)} />
          <Route path={ROUTES.AI_USAGE} element={guard(P.AI_READ, <AiUsagePage />)} />

          <Route path={ROUTES.CATEGORIES} element={guard(P.CATEGORIES_READ, <CategoriesPage />)} />
          <Route path={ROUTES.EXAMS} element={guard(P.EXAMS_READ, <ExamsPage />)} />
          <Route path={ROUTES.ALERTS} element={guard(P.ALERTS_READ, <AlertsListPage />)} />
          <Route path="/alerts/new" element={guard(P.ALERTS_WRITE, <AlertFormPage />)} />
          <Route path="/alerts/:id" element={guard(P.ALERTS_WRITE, <AlertFormPage />)} />
          <Route path={ROUTES.DOWNLOADS} element={guard(P.DOWNLOADS_READ, <DownloadsPage />)} />
          <Route path={ROUTES.BANNERS} element={guard(P.BANNERS_READ, <BannersPage />)} />

          <Route path={ROUTES.PAYMENTS} element={guard(P.PAYMENTS_READ, <PaymentsListPage />)} />
          <Route path={ROUTES.RECURRING_SUBSCRIPTIONS} element={guard(P.PAYMENTS_READ, <RecurringSubscriptionsPage />)} />
          <Route path={ROUTES.SUBSCRIPTIONS} element={guard(P.PAYMENTS_READ, <SubscriptionsPage />)} />
          <Route path={ROUTES.RENEWALS} element={guard(P.PAYMENTS_READ, <RenewalsPage />)} />
          <Route path={ROUTES.WEBHOOK_LOGS} element={guard(P.PAYMENTS_READ, <WebhookLogsPage />)} />
          <Route path={ROUTES.COUPONS} element={guard(P.COUPONS_READ, <CouponsPage />)} />

          <Route path={ROUTES.SETTINGS} element={guard(P.SETTINGS_READ, <SettingsPage />)} />

          <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
